import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ApprovalModule } from "../types";
import { useCreateWorkflow } from "../hooks";
import { moduleLabel, modules } from "../utils";

// Sensible defaults per module — pre-fill everything so user can save immediately.
//
// NOTE 2026-07-10: REQUISITION and PROCUREMENT are two entirely separate entities that both
// happen to say "requisition" — REQUISITION is Staff Requisition (in-stock goods/assets,
// approved by a Store Officer today), PROCUREMENT is Purchase Requisition (external
// procurement needing quotations, approved starting with the Department Head). Keep their
// defaults distinct; don't let the copy for one drift into describing the other.
const MODULE_DEFAULTS: Record<
  ApprovalModule,
  { code: string; name: string; description: string }
> = {
  LEAVE: {
    code: "LEAVE_STANDARD",
    name: "Standard Leave Approval",
    description: "",
  },
  CLAIM: {
    code: "CLAIM_STANDARD",
    name: "Standard Claim Approval",
    description:
      "Default approval workflow for expense claims. Claims are routed through the department head for initial review and the managing director for final sign-off.",
  },
  INVENTORY: {
    code: "INVENTORY_STANDARD",
    name: "Standard Inventory Request Approval",
    description:
      "Default approval workflow for inventory and stock requisitions. Requests are reviewed by the department head before procurement is notified.",
  },
  REQUISITION: {
    code: "REQUISITION_STANDARD",
    name: "Standard Requisition Approval",
    description:
      "Default approval workflow for Staff Requisitions — goods or assets requested out of existing stock. Requests are reviewed by the Store Officer (role-based) before issuing.",
  },
  TRAINING: {
    code: "TRAINING_STANDARD",
    name: "Standard Training Request Approval",
    description:
      "Default approval workflow for training enrollment requests. Requests are reviewed by the department head and approved by the managing director.",
  },
  PROCUREMENT: {
    code: "PROCUREMENT_STANDARD",
    name: "Standard Procurement Requisition Approval",
    description:
      "Default approval workflow for Purchase Requisitions — departments requesting a full purchase (goods, services, or works needing quotations, no prices yet). Requests start with the applicant's Department Head.",
  },
  ASSET: {
    code: "ASSET_DISPOSAL_STANDARD",
    name: "Standard Asset Disposal Approval",
    description:
      "Default approval workflow for asset disposal requests. Requests are reviewed before an asset can be scrapped, donated, or otherwise disposed of.",
  },
};

function extractErrorMessage(err: unknown): string {
  if (!err) return "An unexpected error occurred.";
  const e = err as any;
  return (
    e?.response?.data?.errors?.[0]?.message ??
    e?.response?.data?.message ??
    e?.message ??
    "An unexpected error occurred."
  );
}

function defaultsFor(moduleCode: ApprovalModule) {
  return MODULE_DEFAULTS[moduleCode] ?? MODULE_DEFAULTS.LEAVE;
}

export default function WorkflowCreatePage() {
  const navigate = useNavigate();
  const create = useCreateWorkflow();

  const initialModule: ApprovalModule = "LEAVE";

  const [form, setForm] = useState<{
    moduleCode: ApprovalModule;
    code: string;
    name: string;
    description: string;
    defaultWorkflow: boolean;
    active: boolean;
  }>({
    moduleCode: initialModule,
    ...defaultsFor(initialModule),
    defaultWorkflow: true,
    active: true,
  });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function changeModule(moduleCode: ApprovalModule) {
    setErrorMsg(null);
    setForm((prev) => ({
      ...prev,
      moduleCode,
      ...defaultsFor(moduleCode),
    }));
  }

  async function submit() {
    setErrorMsg(null);
    try {
      const created = await create.mutateAsync(form);
      navigate(`/admin/workflows/${created.id}`);
    } catch (err) {
      setErrorMsg(extractErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <button
        onClick={() => navigate("/admin/workflows")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        ← Back to Workflows
      </button>

      <div>
        <h1 className="text-2xl font-bold">Create Workflow</h1>
        <p className="text-sm text-slate-500">
          Pick a module — the form pre-fills with sensible defaults. Save as-is or edit before creating.
        </p>
      </div>

      {errorMsg && (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {errorMsg}
        </div>
      )}

      <div className="space-y-4 rounded-2xl border bg-white p-5">
        {/* Module picker — changing it resets all defaults */}
        <Field label="Module">
          <div className="flex flex-wrap gap-2">
            {modules.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => changeModule(m)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                  form.moduleCode === m
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-200 text-slate-600 hover:border-emerald-400"
                }`}
              >
                {moduleLabel(m)}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Workflow Code">
          <input
            className="input"
            value={form.code}
            onChange={(e) =>
              setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })
            }
          />
        </Field>

        <Field label="Workflow Name">
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <Field label="Description">
          <textarea
            className="input min-h-28"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>

        <label className="flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm">
          <input
            type="checkbox"
            checked={form.defaultWorkflow}
            onChange={(e) => setForm({ ...form, defaultWorkflow: e.target.checked })}
          />
          <span>
            <span className="font-medium">Set as default workflow</span>
            <span className="ml-1 text-slate-400">
              — all {moduleLabel(form.moduleCode)} requests will use this workflow unless a routing rule matches first
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => navigate("/admin/workflows")}
            className="rounded-xl border px-4 py-2 text-sm"
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={create.isPending}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create Workflow"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </div>
  );
}